import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import RichTextEditor, { RichTextDisplay } from "@/components/RichTextEditor";

const TEMPLATE_KEYS = ["welcome", "message_notification", "invoice_sent", "invoice_reminder"];
const TEMPLATE_LABELS = {
  welcome: "Welcome Email",
  message_notification: "Message Notification",
  invoice_sent: "Invoice Sent",
  invoice_reminder: "Invoice Reminder",
};

const SAMPLE_VARS = {
  welcome: {
    name: "Jane Smith", email: "jane@acmecorp.com", password: "••••••••",
    client_name: "Acme Corp", portal_url: "https://yourdomain.com/login",
    sender_name: "Balbis",
  },
  message_notification: {
    subject_prefix: "", thread_subject: "Website Redesign",
    sender_name: "Alex Johnson", preview: "Hey, just checking in on the project timeline — are we still on track for the launch?",
    link: "#", cta_text: "View Conversation",
  },
  invoice_sent: {
    client_name: "Acme Corp", invoice_number: "INV-042",
    total: "£3,500.00", due_date: "28 Feb 2026", sender_name: "Balbis",
  },
  invoice_reminder: {
    client_name: "Acme Corp", invoice_number: "INV-038",
    total: "£1,200.00", due_date: "14 Jan 2026",
    days_overdue: "14", sender_name: "Balbis",
  },
};

function renderTemplate(str = "", vars = {}) {
  let out = str;
  for (const [k, v] of Object.entries(vars)) {
    out = out.replaceAll(`{{${k}}}`, v ?? "");
  }
  return out;
}

function LivePreview({ subject, body, vars }) {
  const renderedSubject = renderTemplate(subject, vars);
  const renderedBody = renderTemplate(body, vars);
  return (
    <div className="rounded-lg border overflow-hidden" style={{ borderColor: "#E7E5E4" }}>
      <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wider" style={{ background: "#F5F3EF", color: "#A8A29E" }}>
        Live Preview
      </div>
      <div className="p-4" style={{ background: "#FFFBF5" }}>
        <p className="text-xs mb-3" style={{ color: "#78716C" }}>
          <span className="font-semibold" style={{ color: "#292524" }}>Subject: </span>
          {renderedSubject || <em style={{ color: "#A8A29E" }}>using default</em>}
        </p>
        <div
          className="text-sm rounded-lg p-4"
          style={{ background: "#fff", border: "1px solid #E7E5E4", lineHeight: 1.7, color: "#292524" }}
          dangerouslySetInnerHTML={{ __html: renderedBody || "<em style='color:#A8A29E'>using default template body</em>" }}
        />
      </div>
    </div>
  );
}

export default function EmailTemplatesEditor({ currentSettings, onSave, saving }) {
  const [activeTab, setActiveTab] = useState("welcome");
  const [defaults, setDefaults] = useState({});
  const [variables, setVariables] = useState({});
  const [templates, setTemplates] = useState({
    welcome: { subject: "", body: "" },
    message_notification: { subject: "", body: "" },
    invoice_sent: { subject: "", body: "" },
    invoice_reminder: { subject: "", body: "" },
  });

  // Load defaults + variables from API
  useEffect(() => {
    api.get("/settings/email-template-defaults").then((r) => {
      setDefaults(r.data.defaults || {});
      setVariables(r.data.variables || {});
    }).catch(() => {});
  }, []);

  // Seed from current settings
  useEffect(() => {
    const saved = currentSettings?.email_templates || {};
    setTemplates({
      welcome: { subject: saved.welcome?.subject || "", body: saved.welcome?.body || "" },
      message_notification: { subject: saved.message_notification?.subject || "", body: saved.message_notification?.body || "" },
      invoice_sent: { subject: saved.invoice_sent?.subject || "", body: saved.invoice_sent?.body || "" },
      invoice_reminder: { subject: saved.invoice_reminder?.subject || "", body: saved.invoice_reminder?.body || "" },
    });
  }, [currentSettings]);

  const handleSave = () => {
    onSave({ email_templates: templates });
  };

  const tpl = templates[activeTab] || { subject: "", body: "" };
  const def = defaults[activeTab] || {};
  const vars = variables[activeTab] || [];
  const sample = SAMPLE_VARS[activeTab] || {};

  const previewSubject = tpl.subject || def.subject || "";
  const previewBody = tpl.body || def.body || "";

  return (
    <div className="space-y-4">
      {/* Tabs */}
      <div className="flex flex-wrap gap-1">
        {TEMPLATE_KEYS.map((key) => (
          <button
            key={key}
            data-testid={`email-tab-${key}`}
            onClick={() => setActiveTab(key)}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
            style={{
              background: activeTab === key ? "#292524" : "#F5F3EF",
              color: activeTab === key ? "#fff" : "#78716C",
            }}
          >
            {TEMPLATE_LABELS[key]}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Editor column */}
        <div className="space-y-4">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#57534E" }}>
              Subject line
            </Label>
            <p className="text-xs mb-1.5" style={{ color: "#A8A29E" }}>
              Leave blank to use default: <em>{def.subject}</em>
            </p>
            <Input
              data-testid={`subject-input-${activeTab}`}
              placeholder={def.subject || ""}
              value={tpl.subject}
              onChange={(e) => setTemplates((t) => ({ ...t, [activeTab]: { ...tpl, subject: e.target.value } }))}
            />
          </div>

          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#57534E" }}>
              Body
            </Label>
            <p className="text-xs mb-1.5" style={{ color: "#A8A29E" }}>
              Leave blank to use the default body. Use variables below.
            </p>
            <div className="rounded-lg border overflow-hidden" style={{ borderColor: "#E7E5E4" }}>
              <RichTextEditor
                content={tpl.body}
                onChange={(html) => setTemplates((t) => ({ ...t, [activeTab]: { ...tpl, body: html } }))}
                placeholder="Leave blank to use the default template..."
              />
            </div>
          </div>

          {/* Variables */}
          {vars.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "#A8A29E" }}>
                Available variables
              </p>
              <div className="flex flex-wrap gap-1.5">
                {vars.map((v) => (
                  <span
                    key={v}
                    className="text-xs font-mono px-2 py-0.5 rounded cursor-pointer select-all"
                    style={{ background: "#F5F3EF", color: "#D97706", border: "1px solid #E7E5E4" }}
                    title="Click to copy"
                    onClick={() => navigator.clipboard?.writeText(v)}
                  >
                    {v}
                  </span>
                ))}
              </div>
            </div>
          )}

          <Button
            data-testid="save-email-templates-btn"
            onClick={handleSave}
            disabled={saving}
            style={{ background: "#292524", color: "#fff" }}
          >
            {saving ? "Saving..." : "Save Templates"}
          </Button>
        </div>

        {/* Preview column */}
        <div className="space-y-3">
          <LivePreview subject={previewSubject} body={previewBody} vars={sample} />
          <p className="text-xs" style={{ color: "#A8A29E" }}>
            Preview uses sample data. Variables shown in amber above will be replaced with real values when the email is sent.
          </p>
        </div>
      </div>
    </div>
  );
}
